import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ShoppingCart, 
  Utensils, 
  User, 
  FileText, 
  Edit, 
  X, 
  DollarSign, 
  Star, 
  Activity,
  Mail
} from 'lucide-react';

const RecentActivityCard = ({ activities, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Handle the backend response structure which contains activities array
  const getActivitiesList = () => {
    if (!activities) return [];
    
    // If activities is already an array, use it directly
    if (Array.isArray(activities)) {
      return activities;
    }
    
    // If activities has an activities property (backend response structure)
    if (activities.activities && Array.isArray(activities.activities)) {
      return activities.activities;
    }
    
    return [];
  };

  const activitiesList = getActivitiesList();

  const getActivityIcon = (type) => {
    switch (type) {
      case 'NEW_ORDER':
        return <ShoppingCart className="w-4 h-4 text-blue-600" />;
      case 'MEAL_ADDED':
      case 'MEAL_CREATED':
        return <Utensils className="w-4 h-4 text-green-600" />;
      case 'USER_REGISTERED':
        return <User className="w-4 h-4 text-purple-600" />;
      case 'SUBSCRIPTION_CREATED':
        return <FileText className="w-4 h-4 text-orange-600" />;
      case 'MEAL_UPDATED':
        return <Edit className="w-4 h-4 text-yellow-600" />;
      case 'ORDER_CANCELLED':
        return <X className="w-4 h-4 text-red-600" />;
      case 'PAYMENT_RECEIVED':
        return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'CUSTOMER_REVIEW':
        return <Star className="w-4 h-4 text-yellow-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatActivityTime = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));

    if (diffInMinutes < 60) {
      return t('dashboard.minutesAgo', { minutes: diffInMinutes });
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return t('dashboard.hoursAgo', { hours });
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return t('dashboard.daysAgo', { days });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('dashboard.recentActivity')}
        </h3>
        <Activity className="w-6 h-6 text-blue-500" />
      </div>

      {!activities || activitiesList.length === 0 ? (
        <div className="text-center py-8">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">{t('dashboard.noRecentActivity')}</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activitiesList.map((activity, index) => (
            <div key={activity.id || index} className="flex items-start space-x-3 rtl:space-x-reverse">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 font-medium">
                  {activity.title || activity.description}
                </p>
                {activity.description && activity.title && (
                  <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {formatActivityTime(activity.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {activitiesList.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            {t('dashboard.viewAllActivity')}
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentActivityCard;
